import React from "react";
import Navbar from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CustomSwitch } from "@/components/ui/custom-switch";
import {
  ArrowLeft,
  CalendarDays,
  Check,
  Clapperboard,
  Crown,
  DollarSign,
  Heart,
  Info,
  Laptop,
  Loader2,
  MessageCircle,
  Tag,
  Tablet,
  TicketCheck,
  Upload,
  X,
  Zap,
} from "lucide-react";
import {
  getChatModeForCategory,
  getEventExperience,
  VISIBLE_EVENT_CATEGORIES,
} from "@/config/eventCategories";
import { canStartEventCreation } from "@/utils/eventCreation";
import { eventSpansMidnight } from "@/lib/eventTime";

const STEPS = [
  {
    title: "The basics",
    hint: "Name it and tell people what it is.",
    blurb: "Start with the essentials — you can polish everything later.",
    icon: TicketCheck,
  },
  {
    title: "When",
    hint: "Set the date and time.",
    blurb: "Let's lock in your schedule.",
    icon: CalendarDays,
  },
  {
    title: "How it looks",
    hint: "Pick your format and streaming package.",
    blurb: "Choose how your event is framed and produced.",
    icon: Clapperboard,
  },
  {
    title: "Audience & access",
    hint: "Control chat and reactions.",
    blurb: "Decide how viewers interact during your event.",
    icon: MessageCircle,
  },
  {
    title: "Pricing & review",
    hint: "Set your price and confirm the details.",
    blurb: "Almost there — set a price and give it a final look.",
    icon: Check,
  },
];

const FIELD_CLASS =
  "h-11 w-full rounded-[10px] border-[#2a2a2a] bg-[#0f0f0f] px-3.5 text-[15px] text-white placeholder:text-[#5b6270] focus:border-blue-500 focus:ring-1 focus:ring-blue-500";
const LABEL_CLASS = "mb-2 block text-[13px] font-semibold text-[#c9cfdb]";

const ChoiceCard = ({ selected, onClick, children, testId, gold = false }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={[
      "relative rounded-xl border-2 p-[18px] text-left transition-colors",
      selected
        ? gold
          ? "border-yellow-400 bg-yellow-400/10"
          : "border-blue-500 bg-blue-500/10"
        : "border-[#2a2a2a] bg-[#0f0f0f] hover:border-[#454545]",
    ].join(" ")}
    data-testid={testId}
  >
    {children}
  </button>
);

const InteractionToggle = ({ icon: Icon, iconClass, iconBackground, title, description, checked, onChange, testId }) => (
  <div className="rounded-xl border border-[#2a2a2a] bg-[#0f0f0f] px-4 py-4 sm:px-[18px]">
    <div className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-3">
        <div className={"flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] " + iconBackground}>
          <Icon className={"h-[18px] w-[18px] " + iconClass} />
        </div>
        <div className="min-w-0">
          <div className="text-[15px] font-semibold text-white">{title}</div>
          <div className="text-xs leading-5 text-[#8b93a7]">{description}</div>
        </div>
      </div>
      <CustomSwitch
        checked={checked}
        onCheckedChange={onChange}
        aria-label={"Toggle " + title}
        data-testid={testId}
      />
    </div>
  </div>
);

const CreateEventWizard = ({
  user,
  onLogout,
  currentStep,
  setCurrentStep,
  wizardError,
  setWizardError,
  formData,
  setFormData,
  imagePreview,
  handleImageChange,
  promoCode,
  setPromoCode,
  promoDiscount,
  promoValidating,
  validatePromoCode,
  removePromoCode,
  loading,
  geocodingLoading,
  handleSubmit,
  timezoneGroups,
  states,
  showGeoFencing,
}) => {
  const finalSubmitIntent = React.useRef(false);

  const validateStep = (step) => {
    if (step === 1) {
      if (!formData.title.trim()) return "Please enter an event title.";
      if (!formData.description.trim()) return "Add a short description so viewers know what to expect.";
    }
    if (step === 2) {
      if (!formData.date) return "Please choose an event date.";
      if (!formData.start_time || !formData.end_time) return "Please set both a start and end time.";
      if (formData.end_time === formData.start_time) return "Start and end time cannot be the same.";
    }
    if (step === 4 && showGeoFencing && formData.geo_restricted) {
      if (!formData.geo_city.trim() || !formData.geo_state) {
        return "Enter both a city and state for location restriction.";
      }
    }
    if (step === 5 && formData.category !== "Religion") {
      const price = Number(formData.price);
      if (formData.price === "" || Number.isNaN(price) || price < 0) {
        return "Enter a valid ticket price (use 0 for free).";
      }
      if (price > 0 && price < 5) {
        return "Paid tickets must cost at least $5.00 (use $0 for a free event).";
      }
    }
    return "";
  };

  const goToStep = (step) => {
    setWizardError("");
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const continueWizard = () => {
    const error = validateStep(currentStep);
    if (error) {
      setWizardError(error);
      return;
    }
    goToStep(Math.min(5, currentStep + 1));
  };

  // Kept only for compatibility with hidden legacy controls in cached markup.
  const selectOrientation = () => {};

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":").map(Number);
    const suffix = hours >= 12 ? "PM" : "AM";
    return (hours % 12 || 12) + ":" + String(minutes).padStart(2, "0") + " " + suffix;
  };

  const timezoneLabel =
    timezoneGroups.flatMap((group) => group.zones).find((zone) => zone.value === formData.timezone)?.label ||
    formData.timezone;
  const stepMeta = STEPS[currentStep - 1];
  const StepIcon = stepMeta.icon;
  const isReligion = formData.category === "Religion";
  const experience = getEventExperience(formData.category);
  const isProMode = formData.streaming_package === "premium";
  const priceNumber = isReligion ? 0 : Number(formData.price || 0);
  const interactionSummary = [
    formData.chat_enabled ? experience.chatLabel : null,
    formData.reactions_enabled ? experience.reactionsLabel : null,
    formData.tips_enabled ? experience.tipsLabel : null,
  ].filter(Boolean).join(" + ") || "Interactions off";

  return (
    <div className="min-h-screen bg-[#0f0f0f]" data-testid="create-event-page">
      <Navbar user={user} onLogout={onLogout} isCreator={true} />

      <main className="mx-auto w-full max-w-[808px] px-4 pb-16 pt-24 sm:px-6 lg:pt-28">
        <div className="mb-1 flex items-baseline justify-between gap-4">
          <h1 className="text-2xl font-extrabold tracking-tight text-white sm:text-[30px]">Create a new event</h1>
          <span className="shrink-0 text-xs font-semibold text-[#6b7280] sm:text-sm">
            Step {currentStep} of 5
          </span>
        </div>
        <p className="mb-7 text-sm text-[#8b93a7] sm:text-[15px]">{stepMeta.blurb}</p>

        <div className="mb-8 flex items-center" aria-label="Event creation progress">
          {STEPS.map((step, index) => {
            const number = index + 1;
            const complete = number < currentStep;
            const active = number === currentStep;
            return (
              <React.Fragment key={step.title}>
                <button
                  type="button"
                  onClick={() => complete && goToStep(number)}
                  disabled={!complete}
                  aria-label={"Go to step " + number}
                  className={[
                    "flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors",
                    complete
                      ? "border-blue-500 bg-blue-500 text-white"
                      : active
                        ? "border-blue-500 bg-blue-500/15 text-blue-400"
                        : "border-[#2f2f2f] bg-[#161616] text-[#6b7280]",
                  ].join(" ")}
                >
                  {complete ? <Check className="h-4 w-4" /> : number}
                </button>
                {number < STEPS.length && (
                  <div className={"mx-2 h-0.5 flex-1 rounded " + (complete ? "bg-blue-500" : "bg-[#2a2a2a]")} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <form
          onSubmit={(event) => {
            const explicitFinalAction = finalSubmitIntent.current;
            finalSubmitIntent.current = false;

            if (currentStep < 5) {
              event.preventDefault();
              continueWizard();
              return;
            }

            if (!canStartEventCreation({ currentStep, explicitFinalAction, promoValidating })) {
              event.preventDefault();
              setWizardError(
                promoValidating
                  ? "Wait for the promo code to finish validating before creating the event."
                  : "Review your event, then use the button below when you are ready to continue to payment."
              );
              return;
            }
            const error = validateStep(5);
            if (error) {
              event.preventDefault();
              setWizardError(error);
              return;
            }
            setWizardError("");
            handleSubmit(event);
          }}
        >
          <section className="min-h-[380px] rounded-[18px] border border-[#262626] bg-[#161616] p-5 sm:p-8">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-500/15 text-blue-400">
                <StepIcon className="h-[22px] w-[22px]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white sm:text-xl">{stepMeta.title}</h2>
                <p className="mt-0.5 text-xs text-[#8b93a7] sm:text-[13px]">{stepMeta.hint}</p>
              </div>
            </div>

            {currentStep === 1 && (
              <div className="space-y-5">
                <div>
                  <label className={LABEL_CLASS} htmlFor="event-title">Event title</label>
                  <Input
                    id="event-title"
                    value={formData.title}
                    onChange={(event) => {
                      setWizardError("");
                      setFormData((current) => ({ ...current, title: event.target.value }));
                    }}
                    placeholder="e.g. Sunday Night Live Worship"
                    className={FIELD_CLASS}
                    data-testid="title-input"
                  />
                </div>

                <div>
                  <label className={LABEL_CLASS} htmlFor="event-category">Category</label>
                  <select
                    id="event-category"
                    value={formData.category}
                    onChange={(event) => {
                      const category = event.target.value;
                      setFormData((current) => ({
                        ...current,
                        category,
                        price: category === "Religion" ? "0" : current.price,
                        chat_enabled: true,
                        reactions_enabled: true,
                        tips_enabled: true,
                        chat_mode: getChatModeForCategory(category),
                      }));
                    }}
                    disabled={VISIBLE_EVENT_CATEGORIES.length === 1}
                    className={FIELD_CLASS + " appearance-auto"}
                    data-testid="category-select"
                  >
                    {VISIBLE_EVENT_CATEGORIES.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={LABEL_CLASS} htmlFor="event-description">Short description</label>
                  <Textarea
                    id="event-description"
                    value={formData.description}
                    onChange={(event) => {
                      setWizardError("");
                      setFormData((current) => ({ ...current, description: event.target.value }));
                    }}
                    rows={3}
                    placeholder="Tell viewers what to expect in a sentence or two."
                    className="min-h-[78px] w-full resize-y rounded-[10px] border-[#2a2a2a] bg-[#0f0f0f] px-3.5 py-3 text-[15px] text-white placeholder:text-[#5b6270] focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    data-testid="description-input"
                  />
                </div>

                <div>
                  <label className={LABEL_CLASS}>
                    Event poster <span className="font-medium text-[#6b7280]">· optional</span>
                  </label>
                  <label className="group block cursor-pointer overflow-hidden rounded-xl border-2 border-dashed border-[#333] transition-colors hover:border-blue-500/70">
                    {imagePreview ? (
                      <div className="bg-[#0f0f0f] p-3">
                        <img
                          src={imagePreview}
                          alt="Event poster preview"
                          className="mx-auto max-h-64 rounded-lg object-contain"
                        />
                        <div className="mt-2 text-center text-xs font-medium text-[#9aa1af]">
                          Click to replace this image
                        </div>
                      </div>
                    ) : (
                      <div className="px-5 py-7 text-center">
                        <Upload className="mx-auto mb-2 h-7 w-7 text-blue-400" />
                        <div className="text-sm font-semibold text-[#c9cfdb]">Choose an image to upload</div>
                        <div className="mt-1 text-xs text-[#6b7280]">
                          PNG or JPG, up to 10MB · a default is used if you skip
                        </div>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleImageChange}
                      className="hidden"
                      data-testid="image-upload-input"
                    />
                  </label>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className={LABEL_CLASS} htmlFor="event-date">Date</label>
                    <Input
                      id="event-date"
                      type="date"
                      value={formData.date}
                      onChange={(event) => {
                        setWizardError("");
                        setFormData((current) => ({ ...current, date: event.target.value }));
                      }}
                      className={FIELD_CLASS + " showme-blue-picker"}
                      data-testid="date-input"
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLASS} htmlFor="event-timezone">Time zone</label>
                    <select
                      id="event-timezone"
                      value={formData.timezone}
                      onChange={(event) => setFormData((current) => ({ ...current, timezone: event.target.value }))}
                      className={FIELD_CLASS + " appearance-auto"}
                      data-testid="timezone-select"
                    >
                      {timezoneGroups.map((group) => (
                        <optgroup key={group.label} label={group.label}>
                          {group.zones.map((zone) => (
                            <option key={zone.value} value={zone.value}>{zone.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={LABEL_CLASS} htmlFor="event-start">Start time</label>
                    <Input
                      id="event-start"
                      type="time"
                      value={formData.start_time}
                      onChange={(event) => {
                        setWizardError("");
                        setFormData((current) => ({ ...current, start_time: event.target.value }));
                      }}
                      className={FIELD_CLASS + " showme-blue-picker"}
                      data-testid="start-time-input"
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLASS} htmlFor="event-end">End time</label>
                    <Input
                      id="event-end"
                      type="time"
                      value={formData.end_time}
                      onChange={(event) => {
                        setWizardError("");
                        setFormData((current) => ({ ...current, end_time: event.target.value }));
                      }}
                      className={FIELD_CLASS + " showme-blue-picker"}
                      data-testid="end-time-input"
                    />
                    {eventSpansMidnight(formData.start_time, formData.end_time) && (
                      <p className="mt-2 text-xs font-medium text-blue-400" data-testid="overnight-event-note">
                        Ends the following day
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-2.5 rounded-[10px] border border-blue-500/25 bg-blue-500/[0.08] px-3.5 py-3 text-[13px] text-[#9db4e0]">
                  <span aria-hidden="true">💡</span>
                  <p>Your event automatically hides from the schedule once the end time passes — no cleanup needed.</p>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-7">
                <div className="hidden" aria-hidden="true">
                    <button
                      type="button"
                      onClick={() => selectOrientation("portrait")}
                      aria-pressed={formData.orientation === "portrait"}
                      className={[
                        "rounded-xl border-2 px-4 py-5 text-center transition-colors",
                        formData.orientation === "portrait"
                          ? "border-blue-500 bg-blue-500/10"
                          : "border-[#2a2a2a] bg-[#0f0f0f] hover:border-[#454545]",
                      ].join(" ")}
                      data-testid="orientation-portrait"
                    >
                      <div className="mx-auto mb-3 h-14 w-[34px] rounded-[7px] border-2 border-blue-300" />
                      <div className="text-[15px] font-bold text-white">Portrait Mode</div>
                      <div className="mt-0.5 text-xs text-[#8b93a7]">Vertical · phones and social</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => selectOrientation("landscape")}
                      aria-pressed={formData.orientation === "landscape"}
                      className={[
                        "rounded-xl border-2 px-4 py-5 text-center transition-colors",
                        formData.orientation === "landscape"
                          ? "border-blue-500 bg-blue-500/10"
                          : "border-[#2a2a2a] bg-[#0f0f0f] hover:border-[#454545]",
                      ].join(" ")}
                      data-testid="orientation-landscape"
                    >
                      <div className="mx-auto mb-3 h-[34px] w-14 rounded-[7px] border-2 border-blue-300" />
                      <div className="text-[15px] font-bold text-white">Landscape Mode</div>
                      <div className="mt-0.5 text-xs text-[#8b93a7]">Widescreen · cinematic</div>
                    </button>
                </div>

                <div>
                  <p className={LABEL_CLASS}>Streaming package</p>
                  {isProMode && (
                    <div
                      className="mb-3 flex items-start gap-3 rounded-[10px] border border-blue-500/25 bg-blue-500/[0.08] px-3.5 py-3 text-[13px] text-[#a9bee8]"
                      data-testid="creator-device-recommendation"
                    >
                      <div className="mt-0.5 flex shrink-0 items-center gap-1 text-blue-300" aria-hidden="true">
                        <Laptop className="h-4 w-4" />
                        <Tablet className="h-4 w-4" />
                      </div>
                      <p>
                        For better accessibility, use a <strong className="text-blue-200">laptop</strong> or <strong className="text-blue-200">iPad/tablet</strong> to setup the <strong className="text-blue-200">Pro Control Panel</strong> and manage your event.
                      </p>
                    </div>
                  )}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <ChoiceCard
                      selected={!isProMode}
                      onClick={() => setFormData((current) => ({ ...current, streaming_package: "free" }))}
                      testId="package-basic"
                    >
                      <div className="mb-1.5 flex items-center gap-2 text-base font-bold text-white">
                        <Zap className="h-[18px] w-[18px] text-yellow-300" /> Basic Mode
                      </div>
                      <div className="mb-2 text-2xl font-extrabold text-blue-500">Free</div>
                      <p className="text-xs leading-5 text-[#9aa1af]">
                        1 camera · Standard quality · Basic analytics
                      </p>
                    </ChoiceCard>
                    <ChoiceCard
                      selected={isProMode}
                      onClick={() => setFormData((current) => ({ ...current, streaming_package: "premium" }))}
                      testId="package-premium"
                      gold
                    >
                      <span className="absolute -top-2.5 left-4 rounded bg-yellow-400 px-2 py-0.5 text-[10px] font-extrabold tracking-wide text-black">
                        PRO MODE
                      </span>
                      <div className="mb-1.5 flex items-center gap-2 text-base font-bold text-white">
                        <Crown className="h-[18px] w-[18px] text-yellow-400" /> Premium
                      </div>
                      <div className="mb-2 text-2xl font-extrabold text-yellow-400">$1,000</div>
                      <p className="text-xs leading-5 text-[#9aa1af]">
                        Up to 5 cameras · Pro control panel · HD · Live effects
                      </p>
                    </ChoiceCard>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="space-y-4">
                <InteractionToggle
                  icon={MessageCircle}
                  iconClass="text-blue-400"
                  iconBackground="bg-blue-500/15"
                  title={experience.chatLabel}
                  description={experience.chatDescription}
                  checked={formData.chat_enabled}
                  onChange={(checked) => setFormData((current) => ({ ...current, chat_enabled: checked }))}
                  testId="chat-toggle"
                />
                <InteractionToggle
                  icon={Heart}
                  iconClass="text-pink-400"
                  iconBackground="bg-pink-500/15"
                  title={experience.reactionsLabel}
                  description="Floating ❤️ 😂 🔥 👏 reactions during the stream"
                  checked={formData.reactions_enabled}
                  onChange={(checked) => setFormData((current) => ({ ...current, reactions_enabled: checked }))}
                  testId="reactions-toggle"
                />
                {experience.tipsOptional && (
                  <InteractionToggle
                    icon={DollarSign}
                    iconClass="text-green-400"
                    iconBackground="bg-green-500/15"
                    title={experience.tipsLabel}
                    description={`Let viewers send private ${experience.tipSingular.toLowerCase()}s with the ${experience.moneyLabel} green $ effect`}
                    checked={formData.tips_enabled}
                    onChange={(checked) => setFormData((current) => ({ ...current, tips_enabled: checked }))}
                    testId="tips-toggle"
                  />
                )}

                {showGeoFencing && (
                  <div className="rounded-xl border border-[#2a2a2a] bg-[#0f0f0f] px-4 py-4 sm:px-[18px]">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="text-[15px] font-semibold text-white">Location restriction</div>
                        <div className="text-xs text-[#8b93a7]">Only viewers near the event location can watch</div>
                      </div>
                      <CustomSwitch
                        checked={formData.geo_restricted}
                        onCheckedChange={(checked) => setFormData((current) => ({ ...current, geo_restricted: checked }))}
                        aria-label="Toggle location restriction"
                      />
                    </div>
                    {formData.geo_restricted && (
                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        <Input
                          value={formData.geo_city}
                          onChange={(event) =>
                            setFormData((current) => ({
                              ...current,
                              geo_city: event.target.value,
                              latitude: null,
                              longitude: null,
                            }))
                          }
                          placeholder="City"
                          className={FIELD_CLASS}
                        />
                        <select
                          value={formData.geo_state}
                          onChange={(event) =>
                            setFormData((current) => ({
                              ...current,
                              geo_state: event.target.value,
                              latitude: null,
                              longitude: null,
                            }))
                          }
                          className={FIELD_CLASS + " appearance-auto"}
                        >
                          <option value="">Select state</option>
                          {states.map((state) => (
                            <option key={state.code} value={state.code}>{state.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                <p className="px-1 text-xs leading-5 text-[#6b7280]">
                  Interaction features start on for every new event. Optional features can be switched off before continuing.
                </p>
              </div>
            )}

            {currentStep === 5 && (
              <div className="space-y-5">
                <div>
                  <label className={LABEL_CLASS} htmlFor="ticket-price">Ticket price</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[15px] text-[#8b93a7]">$</span>
                    <Input
                      id="ticket-price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={isReligion ? "0" : formData.price}
                      onChange={(event) => {
                        setWizardError("");
                        setFormData((current) => ({ ...current, price: event.target.value }));
                      }}
                      disabled={isReligion}
                      className={FIELD_CLASS + " pl-7 disabled:cursor-not-allowed disabled:text-[#6b7280]"}
                      data-testid="price-input"
                    />
                  </div>
                  <p className="mt-2 text-xs text-[#6b7280]">
                    Set to $0 for a free event. Paid tickets have a $5.00 minimum. Religion category events are always free.
                  </p>
                </div>

                <div className="rounded-xl border border-[#2a2a2a] bg-[#0f0f0f] p-[18px] sm:p-5">
                  <label className="mb-3 flex items-center gap-2 text-sm font-bold text-white" htmlFor="promo-code">
                    <Tag className="h-4 w-4 text-yellow-300" /> Have a promo code?
                  </label>
                  {promoDiscount ? (
                    <div className="flex items-center justify-between gap-3 rounded-[10px] border border-green-500/30 bg-green-500/10 px-3.5 py-3">
                      <div>
                        <div className="text-sm font-bold text-green-400">{promoCode.toUpperCase()} applied</div>
                        <div className="text-xs text-green-300/70">
                          {"You save $" + Number(promoDiscount.discount_amount || 0).toFixed(2)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removePromoCode}
                        className="rounded-lg p-2 text-[#9aa1af] hover:bg-white/5 hover:text-white"
                        aria-label="Remove promo code"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2.5 sm:flex-row">
                      <Input
                        id="promo-code"
                        value={promoCode}
                        onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            if (!promoValidating && !loading && promoCode.trim()) validatePromoCode();
                          }
                        }}
                        placeholder="ENTER CODE"
                        className={FIELD_CLASS + " flex-1 uppercase"}
                        data-testid="promo-code-input"
                      />
                      <button
                        type="button"
                        onClick={validatePromoCode}
                        disabled={promoValidating || loading || !promoCode.trim()}
                        className="flex h-11 items-center justify-center rounded-[10px] bg-green-600 px-6 text-sm font-bold text-white transition-colors hover:bg-green-500 disabled:opacity-60"
                        data-testid="apply-promo-button"
                      >
                        {promoValidating ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" /> Checking...
                          </span>
                        ) : (
                          "Apply"
                        )}
                      </button>
                    </div>
                  )}
                  <p className="mt-2 text-xs text-[#6b7280]">Promo codes apply to Pro Mode purchases.</p>
                </div>

                <div className="rounded-xl border border-[#2a2a2a] bg-[#0f0f0f] p-[18px] sm:p-5">
                  <h3 className="mb-3.5 text-sm font-bold text-white">Review</h3>
                  <dl className="space-y-2.5 text-[13px]">
                    {[
                      ["Event", formData.title || "Untitled event"],
                      ["Category", formData.category],
                      [
                        "When",
                        formData.date
                          ? formData.date +
                            (formData.start_time
                              ? " · " +
                                formatTime(formData.start_time) +
                                "–" +
                                formatTime(formData.end_time) +
                                " " +
                                timezoneLabel
                              : "")
                          : "Not set",
                      ],
                      [
                        "Format",
                        "Portrait Mode · " +
                          (isProMode ? "Pro Mode" : "Basic Mode"),
                      ],
                      ["Audience", interactionSummary],
                    ].map(([label, value]) => (
                      <div key={label} className="flex justify-between gap-4">
                        <dt className="shrink-0 text-[#8b93a7]">{label}</dt>
                        <dd className="text-right font-semibold text-[#e6e8ee]">{value}</dd>
                      </div>
                    ))}
                    <div className="mt-3 flex justify-between border-t border-[#2a2a2a] pt-3">
                      <dt className="text-[#8b93a7]">Ticket</dt>
                      <dd className="font-bold text-blue-400">
                        {priceNumber === 0 ? "Free" : "$" + priceNumber.toFixed(2)}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            )}

            {wizardError && (
              <div
                className="mt-5 flex items-center gap-2.5 rounded-[10px] border border-blue-500/35 bg-blue-500/10 px-3.5 py-3 text-[13px] text-blue-300"
                role="alert"
              >
                <Info className="h-4 w-4 shrink-0 text-blue-400" aria-hidden="true" />
                <p>{wizardError}</p>
              </div>
            )}
          </section>

          <div className="mt-6 flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => currentStep > 1 && goToStep(currentStep - 1)}
              disabled={currentStep === 1}
              className="flex h-11 items-center gap-1.5 rounded-[10px] border border-[#333] bg-transparent px-5 text-sm font-semibold text-[#c9cfdb] transition-colors hover:bg-white/5 disabled:cursor-default disabled:border-[#252525] disabled:text-[#4b4f5a]"
              data-testid="wizard-back"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>

            {currentStep < 5 ? (
              <button
                type="button"
                onClick={continueWizard}
                className="h-11 rounded-[10px] bg-blue-500 px-7 text-sm font-bold text-white transition-colors hover:bg-blue-400"
                data-testid="wizard-continue"
              >
                Continue
              </button>
            ) : (
              <button
                type="submit"
                onClick={() => {
                  finalSubmitIntent.current = true;
                }}
                disabled={loading || geocodingLoading || promoValidating}
                className="flex h-11 min-w-[142px] items-center justify-center rounded-[10px] bg-blue-500 px-6 text-sm font-bold text-white transition-colors hover:bg-blue-400 disabled:cursor-wait disabled:opacity-60"
                data-testid="create-event-submit"
              >
                {promoValidating ? (
                  "Validating promo..."
                ) : loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isProMode ? (
                  promoDiscount?.final_price === 0
                    ? "Create event"
                    : "Create event & continue to payment"
                ) : (
                  "Create event"
                )}
              </button>
            )}
          </div>
        </form>
      </main>
    </div>
  );
};

export default CreateEventWizard;
